/**
 * CanvasReadyGate
 *
 * E018-S009: Ensures canvas is fully initialized before edge system starts.
 * Prevents race conditions during startup.
 */

import type { GfxController } from '@blocksuite/std/gfx';
import type { Store } from '@blocksuite/store';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  type Observable,
  ReplaySubject,
  type Subscription,
  take,
} from 'rxjs';

import type {
  CanvasReadyContext,
  ICanvasReadyGate,
} from '../core/interfaces/ICanvasReadyGate';
import type { ViewportState } from '../types';

// =============================================================================
// Implementation
// =============================================================================

/**
 * Gate that ensures canvas is fully ready before proceeding.
 *
 * Ready conditions:
 * 1. GFX controller is available
 * 2. Viewport is initialized
 * 3. Store is available
 * 4. Layer has received at least one update (hydration complete)
 */
export class CanvasReadyGate implements ICanvasReadyGate {
  private readonly readySubject$ = new ReplaySubject<CanvasReadyContext>(1);
  private readonly isReadySubject$ = new BehaviorSubject<boolean>(false);

  private context: CanvasReadyContext | null = null;
  private subscription: Subscription | null = null;
  private layerSubscription: Subscription | null = null;

  readonly ready$: Observable<CanvasReadyContext> = this.readySubject$
    .asObservable()
    .pipe(take(1));

  get isReady(): boolean {
    return this.isReadySubject$.value;
  }

  /**
   * Creates a new CanvasReadyGate.
   *
   * @param gfx - GFX controller
   * @param store - Block store
   * @param rootId - Root block ID
   */
  constructor(
    private readonly gfx: GfxController,
    private readonly store: Store,
    private readonly rootId: string
  ) {
    this.setupReadyDetection();
  }

  /**
   * Sets up detection for when canvas is ready.
   */
  private setupReadyDetection(): void {
    const viewport = this.gfx.viewport;
    if (!viewport) {
      console.warn('[CanvasReadyGate] No viewport available');
      return;
    }

    // Create observables for each condition
    const viewportReady$ = new BehaviorSubject<ViewportState | null>(
      this.createViewportState(viewport)
    );

    // Subscribe to viewport updates
    this.subscription = viewport.viewportUpdated.subscribe(() => {
      viewportReady$.next(this.createViewportState(viewport));
    });

    // Wait for first layer update (indicates hydration complete)
    const layerReady$ = new BehaviorSubject<boolean>(false);

    // Check if layer already has blocks (might have loaded before we subscribed)
    if (this.gfx.layer.blocks.length > 0) {
      layerReady$.next(true);
    }

    // Subscribe to layer updates
    this.layerSubscription = this.gfx.layer.slots.layerUpdated.subscribe(() => {
      layerReady$.next(true);
    });

    // Combine all conditions
    combineLatest([viewportReady$, layerReady$])
      .pipe(
        filter(([vp, layer]) => vp !== null && layer === true),
        map(([vp]) => this.createContext(vp as ViewportState)),
        take(1)
      )
      .subscribe(context => {
        this.context = context;
        this.isReadySubject$.next(true);
        this.readySubject$.next(context);
      });
  }

  /**
   * Creates viewport state from viewport object.
   */
  private createViewportState(viewport: {
    centerX: number;
    centerY: number;
    zoom: number;
    viewportX: number;
    viewportY: number;
    width: number;
    height: number;
  }): ViewportState {
    return {
      centerX: viewport.centerX,
      centerY: viewport.centerY,
      zoom: viewport.zoom,
      viewportX: viewport.viewportX,
      viewportY: viewport.viewportY,
      width: viewport.width,
      height: viewport.height,
    };
  }

  /**
   * Creates the ready context.
   */
  private createContext(viewport: ViewportState): CanvasReadyContext {
    return {
      gfx: this.gfx,
      viewport,
      store: this.store,
      rootId: this.rootId,
    };
  }

  /**
   * Gets the context if ready.
   */
  getContext(): CanvasReadyContext | null {
    return this.context;
  }

  /**
   * Disposes the gate.
   */
  dispose(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.layerSubscription?.unsubscribe();
    this.layerSubscription = null;
    this.readySubject$.complete();
    this.isReadySubject$.complete();
    this.context = null;
  }
}
